import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { fetchJSON } from 'client/js/util/rest_utils';

const DEFAULT_BASE_URL = '/usercp/filemanager/api';

// Helper function to encode path for URL
const encodePath = (path) => {
  return btoa(path).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const useFileList = (path = '/', baseApiUrl = DEFAULT_BASE_URL) => {
  return useQuery({
    queryKey: ['filemanager', 'list', path, baseApiUrl],
    queryFn: async () => {
      return fetchJSON(`${baseApiUrl}/list?path=${encodeURIComponent(path)}`, {
        method: 'GET',
      });
    },
    staleTime: 30000,
  });
};

export const useCreateItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path, name, type, content }) => {
      return fetchJSON(`${baseApiUrl}`, {
        method: 'POST',
        body: JSON.stringify({ path, name, type, content }),
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.path, baseApiUrl] });
      toast.success(`${variables.type === 'directory' ? 'Ordner' : 'Datei'} erfolgreich erstellt`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useRenameItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path, newName, currentPath }) => {
      const encodedPath = encodePath(path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'PATCH',
        body: JSON.stringify({ action_type: 'rename', new_name: newName }),
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.currentPath, baseApiUrl] });
      toast.success('Erfolgreich umbenannt');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useChmodItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path, mode, currentPath }) => {
      const encodedPath = encodePath(path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'PATCH',
        body: JSON.stringify({ action_type: 'chmod', mode }),
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.currentPath, baseApiUrl] });
      toast.success('Berechtigungen erfolgreich geändert');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path, currentPath, skipRefresh, skipToast }) => {
      const encodedPath = encodePath(path);
      return fetchJSON(`${baseApiUrl}/${encodedPath}`, {
        method: 'DELETE',
      });
    },
    onSuccess: (data, variables) => {
      if (!variables.skipRefresh) {
        queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.currentPath, baseApiUrl] });
      }
      if (!variables.skipToast) {
        toast.success('Erfolgreich gelöscht');
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useCopyItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourcePath, destinationPath, newName, skipRefresh, skipToast }) => {
      return fetchJSON(`${baseApiUrl}/copy`, {
        method: 'POST',
        body: JSON.stringify({ source_path: sourcePath, destination_path: destinationPath, new_name: newName }),
      });
    },
    onSuccess: (data, variables) => {
      if (!variables.skipRefresh) {
        queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.destinationPath, baseApiUrl] });
      }
      if (!variables.skipToast) {
        toast.success('Erfolgreich kopiert');
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useMoveItem = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourcePath, destinationPath, newName, currentPath, skipRefresh, skipToast }) => {
      return fetchJSON(`${baseApiUrl}/move`, {
        method: 'POST',
        body: JSON.stringify({ source_path: sourcePath, destination_path: destinationPath, new_name: newName }),
      });
    },
    onSuccess: (data, variables) => {
      if (!variables.skipRefresh) {
        queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', baseApiUrl] });
      }
      if (!variables.skipToast) {
        toast.success('Erfolgreich verschoben');
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useUploadFile = (baseApiUrl = DEFAULT_BASE_URL) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ path, file, relativePath, skipRefresh }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', path);

      // Add relative path if provided (for folder uploads)
      if (relativePath) {
        formData.append('relative_path', relativePath);
      }

      const response = await fetch(`${baseApiUrl}/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });

      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // Ignore parsing errors
        }

        const message = errorData.message || errorData.error || response.statusText || 'Upload fehlgeschlagen';
        const error = new Error(message);
        error.status = response.status;
        Object.assign(error, errorData);
        throw error;
      }

      return response.json();
    },
    onSuccess: (data, variables) => {
      // Only refresh file list if not skipped (for batch uploads)
      if (!variables.skipRefresh) {
        queryClient.invalidateQueries({ queryKey: ['filemanager', 'list', variables.path, baseApiUrl] });
      }
      // Don't show toast for each file in a folder upload to avoid spam
      if (!variables.relativePath && !variables.skipRefresh) {
        toast.success('Datei erfolgreich hochgeladen');
      }
    },
    onError: (error, variables) => {
      const filename = variables.relativePath || variables.file.name;
      toast.error(`Fehler bei ${filename}: ${error.message}`);
    },
  });
};
